import React, { useState } from "react";
import { useApi, apiPost } from "../hooks/useApi.ts";
import { SearchSelect } from "./SearchSelect.tsx";

interface Project {
  id: number;
  name: string;
  status: string;
  companyName: string;
}

interface ProjectTask {
  id: number;
  name: string;
  dueDate: string;
  status: string;
}

interface Person {
  id: number;
  firstName: string;
  lastName: string;
}

interface Tasklist {
  id: number;
  name: string;
}

interface TaskDateShift {
  id: number;
  name: string;
  oldStartDate: string | null;
  newStartDate: string | null;
  oldDueDate: string | null;
  newDueDate: string | null;
}

interface PushDatesResult {
  success: boolean;
  applied: boolean;
  days: number;
  personTasks?: number;
  tasksUpdated: number;
  skippedCompleted: number;
  updates: TaskDateShift[];
}

function formatShiftDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  const weekday = new Date(`${dateStr}T00:00:00Z`).toLocaleDateString("en-US", {
    weekday: "short",
    timeZone: "UTC",
  });
  return `${weekday} ${dateStr}`;
}

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius)",
  fontSize: "14px",
};

export function DueDateMover() {
  const { data: projects, loading, error } = useApi<Project[]>("/api/projects");
  const [projectId, setProjectId] = useState(0);
  const [target, setTarget] = useState<"task" | "person">("task");

  if (loading) return <div className="loading">Loading projects...</div>;

  if (error) {
    try {
      const parsed = JSON.parse(error);
      if (parsed.error === "api_key_missing") {
        return (
          <div className="card">
            <div className="empty-state">
              <p>Teamwork API key not configured.</p>
              <p style={{ marginTop: "8px" }}>
                Add your Teamwork API key in Settings to move task dates.
              </p>
            </div>
          </div>
        );
      }
    } catch {}
    return <div className="card" style={{ color: "var(--color-danger)" }}>Error: {error}</div>;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>Due Date Mover</h2>
      </div>
      <p style={{ color: "var(--color-text-muted)", fontSize: "13px", marginTop: 0 }}>
        Pushes tasks out by a number of days, or to a new due date. Dates that
        would land on a weekend move to the following Monday, and dependent
        tasks shift by the same extra days so work keeps its spacing.
      </p>
      <div style={{ marginBottom: "16px", maxWidth: "480px" }}>
        <SearchSelect
          options={projects?.map((p) => ({
            value: p.id,
            label: `${p.name} (${p.companyName})`,
          })) || []}
          value={projectId}
          onChange={setProjectId}
          placeholder="-- Select a project --"
        />
      </div>
      {projectId > 0 && (
        <>
          <div style={{ display: "flex", gap: "16px", fontSize: "13px", marginBottom: "16px" }}>
            <span style={{ color: "var(--color-text-muted)" }}>Move:</span>
            <label style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
              <input
                type="radio"
                checked={target === "task"}
                onChange={() => setTarget("task")}
              />
              A single task
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
              <input
                type="radio"
                checked={target === "person"}
                onChange={() => setTarget("person")}
              />
              A person&apos;s tasks
            </label>
          </div>
          {target === "task" ? (
            <TaskShiftPanel key={projectId} projectId={projectId} />
          ) : (
            <PersonShiftPanel key={projectId} projectId={projectId} />
          )}
        </>
      )}
    </div>
  );
}

function ShiftPreview({
  preview,
  applied,
  busy,
  onApply,
}: {
  preview: PushDatesResult;
  applied: boolean;
  busy: boolean;
  onApply: () => void;
}) {
  if (preview.updates.length === 0) {
    return (
      <p style={{ color: "var(--color-text-muted)", fontSize: "13px", marginTop: "16px" }}>
        No open tasks with dates matched — nothing to move.
      </p>
    );
  }

  return (
    <div style={{ marginTop: "16px" }}>
      <p style={{ color: "var(--color-text-muted)", fontSize: "12px", margin: "0 0 8px 0" }}>
        Pushing out by {preview.days} day{preview.days === 1 ? "" : "s"}.
        {preview.personTasks !== undefined &&
          ` ${preview.personTasks} assigned task${preview.personTasks === 1 ? "" : "s"}, ${preview.updates.length} moving in total (including dependents).`}
      </p>
      <table>
        <thead>
          <tr>
            <th>Task</th>
            <th>Current Due</th>
            <th>New Due</th>
          </tr>
        </thead>
        <tbody>
          {preview.updates.map((u) => (
            <tr key={u.id}>
              <td>{u.name}</td>
              <td>{formatShiftDate(u.oldDueDate)}</td>
              <td style={{ fontWeight: 600 }}>{formatShiftDate(u.newDueDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {preview.skippedCompleted > 0 && (
        <p style={{ color: "var(--color-text-muted)", fontSize: "12px", marginTop: "8px" }}>
          {preview.skippedCompleted} completed subtask{preview.skippedCompleted === 1 ? "" : "s"} left unchanged.
        </p>
      )}
      <div style={{ marginTop: "16px", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px" }}>
        {applied ? (
          <span style={{ color: "var(--color-success, #22c55e)", fontSize: "13px" }}>
            Dates updated!
          </span>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={onApply} disabled={busy}>
            {busy ? "Applying..." : `Apply to ${preview.updates.length} task${preview.updates.length === 1 ? "" : "s"}`}
          </button>
        )}
      </div>
    </div>
  );
}

function TaskShiftPanel({ projectId }: { projectId: number }) {
  const { data: tasks, loading } = useApi<ProjectTask[]>(`/api/projects/${projectId}/tasks`);
  const [taskId, setTaskId] = useState(0);
  const [mode, setMode] = useState<"days" | "date">("days");
  const [days, setDays] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [preview, setPreview] = useState<PushDatesResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  const parsedDays = Number(days);
  const inputValid =
    mode === "days"
      ? Number.isInteger(parsedDays) && parsedDays >= 1
      : targetDate.length > 0;

  const reset = () => {
    setPreview(null);
    setApplied(false);
    setError(null);
  };

  const run = async (dryRun: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiPost<PushDatesResult>(`/api/tasks/${taskId}/push-dates`, {
        ...(mode === "days" ? { days: parsedDays } : { targetDate }),
        dryRun,
      });
      setPreview(result);
      if (!dryRun) setApplied(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="loading">Loading tasks...</div>;

  return (
    <div>
      <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
        <SearchSelect
          options={tasks?.map((t) => ({
            value: t.id,
            label: t.dueDate ? `${t.name} (due ${t.dueDate})` : t.name,
          })) || []}
          value={taskId}
          onChange={(val) => {
            setTaskId(val);
            reset();
          }}
          placeholder="-- Select a task --"
          style={{ flex: 1, minWidth: "260px" }}
        />
        <div style={{ display: "flex", gap: "10px", fontSize: "13px", whiteSpace: "nowrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
            <input
              type="radio"
              checked={mode === "days"}
              onChange={() => {
                setMode("days");
                reset();
              }}
            />
            By days
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
            <input
              type="radio"
              checked={mode === "date"}
              onChange={() => {
                setMode("date");
                reset();
              }}
            />
            To a date
          </label>
        </div>
        {mode === "days" ? (
          <input
            type="number"
            min={1}
            placeholder="Days"
            value={days}
            onChange={(e) => {
              setDays(e.target.value);
              reset();
            }}
            style={{ ...inputStyle, width: "90px" }}
          />
        ) : (
          <input
            type="date"
            value={targetDate}
            onChange={(e) => {
              setTargetDate(e.target.value);
              reset();
            }}
            style={{ ...inputStyle, width: "160px" }}
          />
        )}
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => run(true)}
          disabled={busy || !taskId || !inputValid}
        >
          {busy && !preview ? "Loading..." : "Preview"}
        </button>
      </div>

      {error && (
        <p style={{ color: "var(--color-danger)", fontSize: "13px", marginTop: "12px" }}>{error}</p>
      )}

      {preview && (
        <ShiftPreview preview={preview} applied={applied} busy={busy} onApply={() => run(false)} />
      )}
    </div>
  );
}

function PersonShiftPanel({ projectId }: { projectId: number }) {
  const { data: people, loading: loadingPeople } = useApi<Person[]>(
    `/api/projects/${projectId}/people`
  );
  const { data: tasklists, loading: loadingLists } = useApi<Tasklist[]>(
    `/api/projects/${projectId}/tasklists`
  );
  const [personId, setPersonId] = useState(0);
  const [tasklistId, setTasklistId] = useState(0);
  const [days, setDays] = useState("");
  const [preview, setPreview] = useState<PushDatesResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  const parsedDays = Number(days);
  const daysValid = Number.isInteger(parsedDays) && parsedDays >= 1;

  const reset = () => {
    setPreview(null);
    setApplied(false);
    setError(null);
  };

  const run = async (dryRun: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiPost<PushDatesResult>(
        `/api/tasklists/${tasklistId}/push-person-dates`,
        { personId, days: parsedDays, dryRun }
      );
      setPreview(result);
      if (!dryRun) setApplied(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loadingPeople || loadingLists) return <div className="loading">Loading...</div>;

  return (
    <div>
      <p style={{ color: "var(--color-text-muted)", fontSize: "13px", marginTop: 0 }}>
        Moves every open task in the task list that is assigned to this person,
        plus anything that depends on those tasks down the dependency chain.
      </p>
      <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
        <SearchSelect
          options={people?.map((p) => ({
            value: p.id,
            label: `${p.firstName} ${p.lastName}`,
          })) || []}
          value={personId}
          onChange={(val) => {
            setPersonId(val);
            reset();
          }}
          placeholder="-- Select a person --"
          style={{ flex: 1, minWidth: "200px" }}
        />
        <SearchSelect
          options={tasklists?.map((t) => ({ value: t.id, label: t.name })) || []}
          value={tasklistId}
          onChange={(val) => {
            setTasklistId(val);
            reset();
          }}
          placeholder="-- Select a task list --"
          style={{ flex: 1, minWidth: "200px" }}
        />
        <input
          type="number"
          min={1}
          placeholder="Days"
          value={days}
          onChange={(e) => {
            setDays(e.target.value);
            reset();
          }}
          style={{ ...inputStyle, width: "90px" }}
        />
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => run(true)}
          disabled={busy || !personId || !tasklistId || !daysValid}
        >
          {busy && !preview ? "Loading..." : "Preview"}
        </button>
      </div>

      {error && (
        <p style={{ color: "var(--color-danger)", fontSize: "13px", marginTop: "12px" }}>{error}</p>
      )}

      {preview && (
        <ShiftPreview preview={preview} applied={applied} busy={busy} onApply={() => run(false)} />
      )}
    </div>
  );
}
